import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import { oapi } from '@/lib/officialsApi';
import { SettingsCard } from './SettingsCard';
import { AddonSubscribeButton } from './AddonSubscribeButton';

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '');

const statusLine = (s) => {
  if (s.source === 'admin_grant') return `Complimentary access until ${fmt(s.expires_at)}`;
  if (s.status === 'cancelled') return `Auto-renew is off. Access continues until ${fmt(s.expires_at)}.`;
  return `Renews automatically on ${fmt(s.expires_at)}.`;
};

export const AddonStatusCard = () => {
  const { refreshUser } = useAuth();
  const [s, setS] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { oapi('/officials/billing/status').then(setS).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    setBusy(true);
    try {
      const r = await oapi('/officials/billing/cancel', { method: 'POST', body: {} });
      setS(r.addon);
      toast.success('Auto-renew turned off. You keep access until the end of your paid year.');
      refreshUser();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); setConfirm(false); }
  };

  if (!s) return null;
  return (
    <SettingsCard title="Your add-on" testId="settings-addon-status">
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${s.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid="addon-status-pill">
          {s.active ? 'Active' : 'Inactive'}
        </span>
        <span className="text-sm text-slate-600" data-testid="addon-status-line">{statusLine(s)}</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {s.auto_renew && <Button variant="outline" size="sm" onClick={() => setConfirm(true)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="addon-cancel-btn">Turn off auto-renew</Button>}
        {(s.status === 'cancelled' || !s.active) && <AddonSubscribeButton label="Renew for another year" className="h-9" testId="addon-renew-btn" />}
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="addon-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Turn off auto-renew?</AlertDialogTitle>
            <AlertDialogDescription>You keep full access until {fmt(s.expires_at)}. After that, assigning officials stops, but your list and history are kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep auto-renew</AlertDialogCancel>
            <AlertDialogAction onClick={e => { e.preventDefault(); cancel(); }} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="addon-cancel-confirm">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Turn off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsCard>
  );
};
